import { 
  Component, 
  OnInit, 
  OnDestroy, 
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  HostListener,
  ElementRef,
  NgZone,
  Inject,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subscription, combineLatest } from 'rxjs';
import { ScreenRecorderService, RecordingState, StreamWarning } from './screen-recorder.service';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-screen-recorder-indicator',
  templateUrl: './screen-recorder-indicator.component.html',
  styleUrls: ['./screen-recorder-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.theme-light]': 'theme.isLight' }
})
export class ScreenRecorderIndicatorComponent implements OnInit, OnDestroy {
  // State from service
  state: RecordingState = 'idle';
  recordingTime: number = 0;
  uploadProgress: number = 0;
  countdown: number = 0;
  isModalVisible: boolean = false;
  isMinimized: boolean = false;
  isPaused: boolean = false;
  
  // Stream health
  streamWarning: StreamWarning = 'none';
  actualFps: number = 30;

  // Drag state
  isDragging: boolean = false;
  private hasDragged: boolean = false;
  private dragThreshold: number = 5;
  position = { x: 0, y: 0 };
  private dragStart = { x: 0, y: 0 };
  private initialPos = { x: 0, y: 0 };
  
  // Pill dimensions (fixed width for consistency across all states)
  private pillWidth = 140;
  private pillHeight = 34;

  // Visibility flag
  get isVisible(): boolean {
    return this.isMinimized && (
      this.state === 'recording' || 
      this.state === 'paused' ||
      this.state === 'countdown' ||
      this.state === 'processing' ||
      this.state === 'uploading'
    );
  }

  private subscriptions: Subscription = new Subscription();
  private isBrowser: boolean;

  constructor(
    private recorderService: ScreenRecorderService,
    public theme: ThemeService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Subscribe to service state
    this.subscriptions.add(
      combineLatest([
        this.recorderService.state$,
        this.recorderService.recordingTime$,
        this.recorderService.uploadProgress$,
        this.recorderService.countdown$,
        this.recorderService.isModalVisible$,
        this.recorderService.isMinimized$,
        this.recorderService.isPaused$,
        this.recorderService.streamWarning$,
        this.recorderService.actualFps$
      ]).subscribe(([state, recordingTime, uploadProgress, countdown, isModalVisible, isMinimized, isPaused, streamWarning, actualFps]) => {
        const wasVisible = this.isVisible;
        
        this.state = state;
        this.recordingTime = recordingTime;
        this.uploadProgress = uploadProgress;
        this.countdown = countdown;
        this.isModalVisible = isModalVisible;
        this.isMinimized = isMinimized;
        this.isPaused = isPaused;
        this.streamWarning = streamWarning;
        this.actualFps = actualFps;
        
        // Position in top-right when becoming visible
        if (!wasVisible && this.isVisible && this.isBrowser) {
          this.setTopRightPosition();
        }
        
        this.cdr.markForCheck();
      })
    );

    // Set up drag listeners (browser only)
    if (this.isBrowser) {
      this.ngZone.runOutsideAngular(() => {
        document.addEventListener('mousemove', this.onMouseMove.bind(this));
        document.addEventListener('mouseup', this.onMouseUp.bind(this));
        document.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        document.addEventListener('touchend', this.onTouchEnd.bind(this));
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.isBrowser) {
      document.removeEventListener('mousemove', this.onMouseMove.bind(this));
      document.removeEventListener('mouseup', this.onMouseUp.bind(this));
      document.removeEventListener('touchmove', this.onTouchMove.bind(this));
      document.removeEventListener('touchend', this.onTouchEnd.bind(this));
    }
  }

  /**
   * Position the pill in the top-right corner (near utilities panel button)
   */
  private setTopRightPosition(): void {
    // Position: 24px from top, 24px from right (aligns with vertical ellipsis button area)
    const topPadding = 24;
    const rightPadding = 24;
    this.position = {
      x: window.innerWidth - this.pillWidth - rightPadding,
      y: topPadding
    };
  }

  /**
   * Center the pill at the bottom of the screen (legacy, kept for reference)
   */
  private centerPosition(): void {
    const padding = 20;
    this.position = {
      x: (window.innerWidth - this.pillWidth) / 2,
      y: window.innerHeight - this.pillHeight - padding
    };
  }

  /**
   * Handle window resize - keep pill within bounds
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isBrowser) return;
    this.constrainToBounds();
    this.cdr.markForCheck();
  }

  /**
   * Constrain position to window bounds
   */
  private constrainToBounds(): void {
    const padding = 10;
    const maxX = window.innerWidth - this.pillWidth - padding;
    const maxY = window.innerHeight - this.pillHeight - padding;
    this.position.x = Math.max(padding, Math.min(this.position.x, maxX));
    this.position.y = Math.max(padding, Math.min(this.position.y, maxY));
  }

  /**
   * Start dragging (or potential click)
   */
  onDragStart(event: MouseEvent | TouchEvent): void {
    if ((event.target as HTMLElement).closest('button')) {
      return; // Don't drag when clicking buttons
    }
    
    event.preventDefault();
    this.isDragging = true;
    this.hasDragged = false; // Reset - we'll set this true if they actually move
    
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    
    this.dragStart = { x: clientX, y: clientY };
    this.initialPos = { ...this.position };
  }

  /**
   * Handle mouse move during drag
   */
  private onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    
    const deltaX = event.clientX - this.dragStart.x;
    const deltaY = event.clientY - this.dragStart.y;
    
    this.updatePosition(deltaX, deltaY);
  }

  /**
   * Handle touch move during drag
   */
  private onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    
    event.preventDefault();
    const touch = event.touches[0];
    const deltaX = touch.clientX - this.dragStart.x;
    const deltaY = touch.clientY - this.dragStart.y;
    
    this.updatePosition(deltaX, deltaY);
  }

  /**
   * Update position during drag
   */
  private updatePosition(deltaX: number, deltaY: number): void {
    // Check if we've moved beyond the drag threshold
    if (!this.hasDragged && (Math.abs(deltaX) > this.dragThreshold || Math.abs(deltaY) > this.dragThreshold)) {
      this.hasDragged = true;
    }
    
    const padding = 10;
    const maxX = window.innerWidth - this.pillWidth - padding;
    const maxY = window.innerHeight - this.pillHeight - padding;
    
    this.position.x = Math.max(padding, Math.min(this.initialPos.x + deltaX, maxX));
    this.position.y = Math.max(padding, Math.min(this.initialPos.y + deltaY, maxY));
    
    this.ngZone.run(() => this.cdr.markForCheck());
  }

  /**
   * End dragging (mouse) - if no drag occurred, treat as click to expand
   */
  private onMouseUp(): void {
    if (this.isDragging && !this.hasDragged) {
      // It was a click, not a drag - restore the modal
      this.ngZone.run(() => this.restore());
    }
    this.isDragging = false;
    this.hasDragged = false;
  }

  /**
   * End dragging (touch) - if no drag occurred, treat as tap to expand
   */
  private onTouchEnd(): void {
    if (this.isDragging && !this.hasDragged) {
      // It was a tap, not a drag - restore the modal
      this.ngZone.run(() => this.restore());
    }
    this.isDragging = false;
    this.hasDragged = false;
  }

  /**
   * Restore the modal
   */
  restore(): void {
    this.recorderService.restoreModal();
  }

  /**
   * Pause recording
   */
  pauseRecording(): void {
    this.recorderService.pauseRecording();
  }

  /**
   * Resume recording
   */
  resumeRecording(): void {
    this.recorderService.resumeRecording();
  }
  
  /**
   * Toggle pause/resume
   */
  togglePause(): void {
    if (this.isPaused) {
      this.recorderService.resumeRecording();
    } else {
      this.recorderService.pauseRecording();
    }
  }

  /**
   * Stop recording and upload
   */
  stopRecording(): void {
    this.recorderService.stopRecording();
    this.recorderService.restoreModal();
  }

  /**
   * Discard recording without uploading
   */
  discardRecording(): void {
    this.recorderService.discardRecording();
  }

  /**
   * Format time for display
   */
  formatTime(seconds: number): string {
    return this.recorderService.formatTime(seconds);
  }

  /**
   * Get status text - always show time for recording/paused states
   */
  get statusText(): string {
    switch (this.state) {
      case 'countdown': return 'Starting...';
      case 'recording': return this.formatTime(this.recordingTime);
      case 'paused': return this.formatTime(this.recordingTime); // Show time, not "Paused"
      case 'processing': return 'Processing...';
      case 'uploading': return `${this.uploadProgress}%`;
      default: return '';
    }
  }
  
  /**
   * Check if we have a warning to show
   */
  get hasWarning(): boolean {
    return this.streamWarning !== 'none';
  }
  
  /**
   * Get warning tooltip text
   */
  get warningText(): string {
    switch (this.streamWarning) {
      case 'screen-frozen': return 'Screen appears frozen - attempting recovery';
      case 'webcam-frozen': return 'Webcam appears frozen';
      case 'audio-lost': return 'Audio track lost';
      case 'low-framerate': return `Low framerate (${this.actualFps} fps)`;
      default: return '';
    }
  }

  /**
   * Keyboard shortcuts
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    if (!this.isVisible) return;

    // Escape key stops recording
    if (event.key === 'Escape' && (this.state === 'recording' || this.state === 'paused')) {
      event.preventDefault();
      this.stopRecording();
    }

    // Space key toggles pause (when not in an input)
    if (event.key === ' ' && !this.isInputFocused() && (this.state === 'recording' || this.state === 'paused')) {
      event.preventDefault();
      if (this.isPaused) {
        this.resumeRecording();
      } else {
        this.pauseRecording();
      }
    }
  }

  /**
   * Check if an input element is focused
   */
  private isInputFocused(): boolean {
    const activeElement = document.activeElement;
    return activeElement instanceof HTMLInputElement || 
           activeElement instanceof HTMLTextAreaElement ||
           activeElement instanceof HTMLSelectElement ||
           (activeElement as HTMLElement)?.isContentEditable;
  }
}
